/**
 * The cache identity of a media URL: the URL without its delivery signature.
 *
 * AUTHZ-02 signs media as `/media/<file>?v=<mtime>&exp=<epoch>&kid=<id>&sig=<b64url>`, and
 * `exp` (and with it `sig`) changes at least once a day for the same bytes. Every place that
 * stores or compares media by URL — Workbox cache keys in `public/sw.js`, the prefetch's Cache
 * API writes and lookups, the native `urlMap` and its Dexie index, the SVG plan caches, and the
 * "is this a different file?" comparisons — keys by this value. Keyed by the raw URL, a
 * re-signed URL would miss a cache that already holds the file: offline, a missing photo.
 *
 * Contract (pinned by `__tests__/mediaCacheKey.test.ts` and the per-site contract tests):
 * - every `exp`, `kid` and `sig` query parameter is dropped (all occurrences, any position),
 *   and so is the fragment;
 * - every other parameter, `v` included, is kept byte for byte and in its original order —
 *   `v` is the content version, so a changed `v` is a different entry;
 * - a relative URL stays relative and an absolute one stays absolute — nothing is resolved
 *   against a base, so `urlMap` (relative keys) and Workbox (absolute keys) each keep their form;
 * - a URL with no signature parameter and no fragment is returned unchanged, which is why the
 *   entries stored before signing existed need no migration.
 *
 * Parameter names are matched as written (no percent-decoding, case-sensitive). A name spelled
 * any other way is not the signature the server reads, so keeping it is the safe direction: the
 * worst case is a cache miss, never two files under one key.
 *
 * Dependency-free on purpose: the service worker imports this module.
 */
const SIGNATURE_PARAMS: ReadonlySet<string> = new Set(['exp', 'kid', 'sig'])

export function mediaCacheKey(url: string): string {
  const fragmentAt = url.indexOf('#')
  const withoutFragment = fragmentAt === -1 ? url : url.slice(0, fragmentAt)

  const queryAt = withoutFragment.indexOf('?')
  if (queryAt === -1) return withoutFragment

  const pairs = withoutFragment.slice(queryAt + 1).split('&')
  const kept = pairs.filter(pair => !SIGNATURE_PARAMS.has(paramName(pair)))
  if (kept.length === pairs.length) return withoutFragment

  const path = withoutFragment.slice(0, queryAt)
  return kept.length === 0 ? path : `${path}?${kept.join('&')}`
}

function paramName(pair: string): string {
  const equalsAt = pair.indexOf('=')
  return equalsAt === -1 ? pair : pair.slice(0, equalsAt)
}
